// Single entry that decides what the ColoringPage is painting on this mount.
//
// Three sources of truth feed in:
//   1. URL path  — /pack/:packId/color/:itemId  vs.  /pack/:packId/bonus
//   2. packId / itemId params
//   3. The isBonusId(itemId, packId) discriminator from @skena/schema
//
// Output mode controls render + API behavior downstream:
//   • 'item'   — regular item, ColoringPage opens artwork with itemId
//   • 'bonus'  — bonus full-scene, ColoringPage opens artwork with bonusId
//   • 'bonus-redirect-needed' — user landed on /color/<bonusId>; signal a
//     redirect to /pack/:packId/bonus so the bonus-specific gate runs
//   • 'invalid' — packId missing / unknown OR item route with missing itemId
//
// Why a hook (not a derived value inline in ColoringPage): the same logic
// drives mount-time redirect (task 2.4), API open payload (task 2.5), and
// the share-button isCapstone flag (task 5.4). Having one source prevents
// drift between those callsites.

import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { assetUrl, bonusAssetUrl, isBonusId, isPackId, PACKS, type PackId } from '@skena/schema'

export type ColoringMode = 'item' | 'bonus' | 'bonus-redirect-needed' | 'invalid'

export interface ColoringTarget {
  mode: ColoringMode
  packId: PackId | null
  /** itemId we'll use as the artwork's itemId for open + autosave. */
  artworkItemId: string | null
  /** Static lineart URL to paint as the canvas substrate. */
  lineartUrl: string | null
}

/**
 * Pure resolver — splits decision-making out of the React hook so it can be
 * unit-tested without renderHook / a Router wrapper. The hook below is a
 * thin adapter over useParams + useLocation.
 */
export function resolveColoringTarget(
  packId: string | undefined,
  itemId: string | undefined,
  pathname: string,
): ColoringTarget {
  if (!packId || !isPackId(packId)) {
    return { mode: 'invalid', packId: null, artworkItemId: null, lineartUrl: null }
  }
  const pid = packId as PackId

  // /pack/:packId/bonus — the bonus route. itemId param is absent.
  if (pathname === `/pack/${pid}/bonus`) {
    const bonusId = PACKS[pid].bonus.id
    return {
      mode: 'bonus',
      packId: pid,
      artworkItemId: bonusId,
      lineartUrl: bonusAssetUrl(bonusId, 'lineart'),
    }
  }

  // /pack/:packId/color/:itemId — guard against a bonusId sneaking into
  // the item route. Spec D2 risk mitigation: one path → one mode.
  if (!itemId) {
    return { mode: 'invalid', packId: pid, artworkItemId: null, lineartUrl: null }
  }
  if (isBonusId(itemId, pid)) {
    return {
      mode: 'bonus-redirect-needed',
      packId: pid,
      artworkItemId: null,
      lineartUrl: null,
    }
  }

  return {
    mode: 'item',
    packId: pid,
    artworkItemId: itemId,
    lineartUrl: assetUrl(pid, itemId, 'lineart'),
  }
}

export function useColoringTarget(): ColoringTarget {
  const { packId, itemId } = useParams<{ packId: string; itemId?: string }>()
  const { pathname } = useLocation()
  return useMemo(() => resolveColoringTarget(packId, itemId, pathname), [packId, itemId, pathname])
}
